<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-4 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/profile')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-key-variant me-2"></i>{{ $t('page.change_password', 'Change Password') }}</h4>
        </div>
      </div>

      <div class="row justify-content-center">
        <div class="col-lg-6 col-md-8">
          <div class="hms-card">
            <div v-if="success" class="hms-alert hms-alert-success">
              <i class="mdi mdi-check-circle me-1"></i>Password changed successfully.
            </div>
            <div v-if="error" class="hms-alert hms-alert-danger">
              <i class="mdi mdi-alert-circle me-1"></i>{{ error }}
            </div>

            <div class="row g-3 mb-3">
              <div class="col-12"><label class="hms-label">Current Password *</label>
                <div class="hms-input-row">
                  <input class="hms-input" :type="showOld ? 'text' : 'password'" v-model="oldPassword" autocomplete="current-password" />
                  <button class="hms-btn hms-btn-blue" type="button" @click="showOld=!showOld"><i class="mdi" :class="showOld ? 'mdi-eye-off' : 'mdi-eye'"></i></button>
                </div>
              </div>
              <div class="col-12"><label class="hms-label">New Password *</label>
                <div class="hms-input-row">
                  <input class="hms-input" :type="showNew ? 'text' : 'password'" v-model="newPassword" autocomplete="new-password" />
                  <button class="hms-btn hms-btn-blue" type="button" @click="showNew=!showNew"><i class="mdi" :class="showNew ? 'mdi-eye-off' : 'mdi-eye'"></i></button>
                </div>
                <div class="hms-strength-row mt-2">
                  <div class="hms-strength-bar" :class="strengthClass"></div>
                  <span class="hms-muted-sm">{{ strengthLabel }}</span>
                </div>
                <ul class="hms-checklist mt-2">
                  <li :class="check.length    ? 'ok' : ''"><i class="mdi" :class="check.length    ? 'mdi-check' : 'mdi-circle-small'"></i>At least 8 characters</li>
                  <li :class="check.upper     ? 'ok' : ''"><i class="mdi" :class="check.upper     ? 'mdi-check' : 'mdi-circle-small'"></i>One uppercase letter</li>
                  <li :class="check.lower     ? 'ok' : ''"><i class="mdi" :class="check.lower     ? 'mdi-check' : 'mdi-circle-small'"></i>One lowercase letter</li>
                  <li :class="check.digit     ? 'ok' : ''"><i class="mdi" :class="check.digit     ? 'mdi-check' : 'mdi-circle-small'"></i>One digit</li>
                  <li :class="check.special   ? 'ok' : ''"><i class="mdi" :class="check.special   ? 'mdi-check' : 'mdi-circle-small'"></i>One special character (recommended)</li>
                </ul>
              </div>
              <div class="col-12"><label class="hms-label">Confirm New Password *</label>
                <input class="hms-input" :type="showNew ? 'text' : 'password'" v-model="confirmPassword" autocomplete="new-password" />
                <div class="hms-muted-sm mt-1" v-if="confirmPassword && newPassword !== confirmPassword">
                  <span class="text-danger"><i class="mdi mdi-alert-circle"></i> Passwords do not match.</span>
                </div>
              </div>
            </div>

            <button class="hms-btn hms-btn-primary w-100" @click="submit" :disabled="processing || !canSubmit">
              <span v-if="processing" class="spinner-border spinner-border-sm me-1"></span>
              {{ processing ? 'Updating...' : 'Update Password' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style>
:root, html[data-theme="dark"] {
  --hms-h:#f0f0f0;--hms-s:#a0a0a0;--hms-m:#808080;
  --hms-bg1:#141414;--hms-bg2:#0a0a0a;--hms-bg3:#1e1e1e;--hms-bg4:#0f0f0f;
  --hms-br:#2a2a2a;--hms-input-br:#333333;
  --hms-primary:#1a7fe8;--hms-primary-lt:rgba(26,127,232,0.12);
  --hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;
}
html[data-theme="light"] {
  --hms-h:#1a1a1a;--hms-s:#555555;--hms-m:#777777;
  --hms-bg1:#ffffff;--hms-bg2:#f4f6f9;--hms-bg3:#eef1f5;--hms-bg4:#ffffff;
  --hms-br:#dee2e8;--hms-input-br:#ced4da;
  --hms-primary:#0047AB;--hms-primary-lt:rgba(0,71,171,0.08);
  --hms-success:#28a745;--hms-danger:#dc3545;--hms-warning:#ffc107;
}
html[data-theme="light"] body, html[data-theme="dark"] body { background: var(--hms-bg2) !important; color: var(--hms-h) !important; }
</style>

<style scoped>
.hms-page { min-height: 100vh; background: var(--hms-bg2); padding-top: 70px; color: var(--hms-h); }
.hms-page-body { padding-top: 20px; padding-bottom: 40px; }
.hms-page-header { flex-wrap: wrap; gap: 12px; }
.hms-back { color: var(--hms-s); font-size: 22px; padding: 4px; }
.hms-back:hover { color: var(--hms-primary); }
.hms-page-title { color: var(--hms-h); font-size: 20px; font-weight: 700; }

.hms-muted-sm { color: var(--hms-s); font-size: 12px; }
.hms-card { background: var(--hms-bg1); border: 1px solid var(--hms-br); border-radius: 14px; padding: 24px; }
.hms-input { width: 100%; background: var(--hms-bg4); border: 1px solid var(--hms-input-br); border-radius: 8px; padding: 8px 14px; color: var(--hms-h); font-size: 14px; outline: none; }
.hms-input:focus { border-color: var(--hms-primary); }
.hms-input-row { display: flex; gap: 8px; align-items: center; }
.hms-label { color: var(--hms-m); font-size: 12px; font-weight: 600; margin-bottom: 4px; display: block; }

.hms-btn { border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; transition: all .15s; display: inline-flex; align-items: center; justify-content: center; }
.hms-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.hms-btn-primary { background: var(--hms-primary); color: #fff; }
.hms-btn-primary:hover:not(:disabled) { filter: brightness(1.1); }
.hms-btn-blue { background: var(--hms-primary-lt); color: var(--hms-primary); border: 1px solid var(--hms-primary-lt); }
.hms-btn-blue:hover:not(:disabled) { background: var(--hms-primary); color: #fff; }

.hms-alert { padding: 10px 14px; border-radius: 8px; font-size: 13px; margin-bottom: 16px; }
.hms-alert-success { background: rgba(40,167,69,.1); color: var(--hms-success); border: 1px solid rgba(40,167,69,.3); }
.hms-alert-danger { background: rgba(220,53,69,.1); color: var(--hms-danger); border: 1px solid rgba(220,53,69,.3); }

.hms-strength-row { display: flex; align-items: center; gap: 10px; }
.hms-strength-bar { flex: 1; height: 6px; border-radius: 4px; background: var(--hms-bg3); position: relative; overflow: hidden; }
.hms-strength-bar::after { content: ''; position: absolute; left: 0; top: 0; bottom: 0; width: 0%; background: var(--hms-danger); transition: all .25s; }
.hms-strength-bar.s1::after { width: 25%; background: var(--hms-danger); }
.hms-strength-bar.s2::after { width: 50%; background: #f59e0b; }
.hms-strength-bar.s3::after { width: 75%; background: var(--hms-primary); }
.hms-strength-bar.s4::after { width: 100%; background: var(--hms-success); }

.hms-checklist { list-style: none; padding: 0; margin: 0; display: grid; grid-template-columns: 1fr 1fr; gap: 4px 12px; }
.hms-checklist li { font-size: 12px; color: var(--hms-s); display: flex; align-items: center; gap: 4px; }
.hms-checklist li.ok { color: var(--hms-success); }

.text-danger { color: var(--hms-danger); }
</style>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "ChangePassword",
  components: { Navigation },
  data() {
    return {
      oldPassword: "", newPassword: "", confirmPassword: "",
      showOld: false, showNew: false,
      processing: false,
      success: false, error: ""
    };
  },
  computed: {
    check() {
      var p = this.newPassword || "";
      return {
        length:  p.length >= 8,
        upper:   /[A-Z]/.test(p),
        lower:   /[a-z]/.test(p),
        digit:   /[0-9]/.test(p),
        special: /[^A-Za-z0-9]/.test(p)
      };
    },
    strength() {
      var c = this.check; var n = 0;
      if (c.length) n++; if (c.upper) n++; if (c.lower) n++; if (c.digit) n++; if (c.special) n++;
      return Math.min(4, Math.max(0, n - 1));
    },
    strengthClass() { return this.newPassword ? ('s' + this.strength) : ''; },
    strengthLabel() {
      if (!this.newPassword) return "Enter a new password";
      return ["Too weak","Weak","Fair","Good","Strong"][this.strength];
    },
    canSubmit() {
      return !!this.oldPassword
          && !!this.newPassword
          && this.newPassword === this.confirmPassword
          && this.check.length;
    }
  },
  mounted() {
    if (!sessionStorage.getItem("isLoggedIn")) { this.$router.push("/login"); return; }
    this.applyTheme();
  },
  methods: {
    applyTheme() {
      var t = localStorage.getItem("hms_theme") || document.documentElement.getAttribute("data-theme") || "light";
      document.documentElement.setAttribute("data-theme", t);
    },
    async submit() {
      this.error = ""; this.success = false;
      if (!this.canSubmit) { this.error = "Please complete all fields and meet the password requirements."; return; }
      this.processing = true;
      try {
        var r = await executeJson({ function: "changePassword", old_password: this.oldPassword, new_password: this.newPassword }, hms);
        if (r.data && r.data.success) {
          this.success = true;
          this.oldPassword = ""; this.newPassword = ""; this.confirmPassword = "";
          var self = this;
          setTimeout(() => { self.$router.push("/profile"); }, 1200);
        } else { this.error = (r.data && r.data.message) || "Failed to update password."; }
      } catch(e) { this.error = "Connection error."; }
      this.processing = false;
    }
  }
};
</script>
